'use client';

import Link from 'next/link';
import { useCallback, useRef } from 'react';

import { useHeaderDropdownPosition } from '@/components/header/useHeaderDropdownPosition';
import { useHoverMenuOpen } from '@/components/header/useHoverMenuOpen';
import { countryHref, triggerClass } from '@/components/header/navShared';
import { useDestinationsMenu } from '@/components/header/useDestinationsMenu';

/** Same vertical padding as continent rows (`py-2.5`) so spacing matches */
const countryLinkClass =
  'block px-3 py-2.5 text-sm font-inter font-light text-gray-200 transition-colors hover:bg-gray-800/80 hover:text-primary-400';

/** Match search bar select labels (All activities / All locations): text-md, Inter, white */
const columnHeadingClass =
  'border-b border-gray-700/80 px-3 py-2 text-md font-inter font-normal text-white';

type DestinationsNavDropdownProps = {
  active: boolean;
  menuId: string;
};

export function DestinationsNavDropdown({ active, menuId }: DestinationsNavDropdownProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { topPx, rightPx, anchorRightPx, measureSync } = useHeaderDropdownPosition(triggerRef, {
    alignMenuRightEdgeToTriggerRight: true,
  });
  const { open, openMenu, scheduleClose } = useHoverMenuOpen();

  const {
    content,
    loading,
    loadError,
    continents,
    selectedContinent,
    setSelectedContinentId,
    tourCountries,
    visibleCountries: rightColumnCountries,
  } = useDestinationsMenu();

  const handleWrapperEnter = useCallback(() => {
    openMenu();
    measureSync();
  }, [measureSync, openMenu]);

  const menuVisibility =
    open
      ? 'pointer-events-auto visible translate-y-0 opacity-100 duration-200 ease-out'
      : 'pointer-events-none invisible -translate-y-[5px] opacity-0 duration-100 ease-in';

  return (
    <div
      className="relative inline-flex shrink-0 items-center"
      onMouseEnter={handleWrapperEnter}
      onMouseLeave={scheduleClose}
    >
      <button
        ref={triggerRef}
        type="button"
        className={triggerClass(active)}
        aria-expanded={open}
        aria-haspopup="menu"
        id={`${menuId}-trigger`}
      >
        Destinations
      </button>
      {/* Right edge aligned with end of “Destinations” (letter s); panel extends leftward */}
      <div
        className={`fixed z-[1200] max-h-[min(70vh,calc(100vh-5rem))] overflow-y-auto pt-0 transition-[opacity,transform,visibility] motion-reduce:transition-none ${menuVisibility}`}
        role="menu"
        aria-labelledby={`${menuId}-trigger`}
        id={`${menuId}-menu`}
        style={{
          top: topPx != null ? `${topPx}px` : undefined,
          left: 'auto',
          right: rightPx != null ? `${rightPx}px` : undefined,
          width: 'min(56vw, 64rem)',
          maxWidth:
            anchorRightPx != null
              ? `min(56vw, 64rem, ${Math.max(0, anchorRightPx - 16)}px)`
              : undefined,
        }}
        onMouseEnter={openMenu}
        onMouseLeave={scheduleClose}
      >
        <div className="w-full overflow-hidden rounded-lg border border-gray-700 bg-gray-900/95 shadow-xl backdrop-blur-md">
          {loading && (
            <div className="flex min-h-[16rem] items-center justify-center px-6 py-10">
              <p className="text-sm text-gray-500">Loading destinations…</p>
            </div>
          )}

          {!loading && loadError && (
            <div className="flex min-h-[10rem] items-center justify-center px-6 py-8">
              <p className="text-center text-sm text-gray-500">Could not load destinations.</p>
            </div>
          )}

          {!loading && !loadError && content && continents.length === 0 && tourCountries.length === 0 && (
            <div className="flex min-h-[10rem] items-center justify-center px-6 py-8">
              <p className="text-center text-sm text-gray-500">No destinations available.</p>
            </div>
          )}

          {!loading && !loadError && content && (continents.length > 0 || tourCountries.length > 0) && (
            <div className="grid min-h-[16rem] max-h-[min(70vh,26rem)] grid-cols-1 sm:grid-cols-2">
              {continents.length > 0 ? (
                <>
                  <div className="flex flex-col border-gray-700/80 sm:border-r">
                    <p className={columnHeadingClass}>Continents</p>
                    <ul className="m-0 list-none overflow-y-auto p-0" role="none">
                      {continents.map((c) => (
                        <li key={c.id} role="none">
                          <button
                            type="button"
                            role="menuitem"
                            className={`w-full px-3 py-2.5 text-left text-sm font-inter font-light transition-colors ${
                              selectedContinent?.id === c.id
                                ? 'bg-gray-800/90 text-primary-400'
                                : 'text-gray-200 hover:bg-gray-800/70'
                            }`}
                            onMouseEnter={() => setSelectedContinentId(c.id)}
                            onFocus={() => setSelectedContinentId(c.id)}
                          >
                            {c.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex min-h-0 flex-col border-t border-gray-700/80 sm:border-t-0">
                    <p className={columnHeadingClass}>Countries</p>
                    <ul
                      className="m-0 grid flex-1 auto-rows-min list-none grid-cols-1 gap-x-4 gap-y-0 overflow-y-auto p-0 sm:grid-cols-2 sm:items-start"
                      role="none"
                    >
                      {rightColumnCountries.length === 0 ? (
                        <li className="col-span-full px-3 py-4 text-sm text-gray-500">
                          No countries in this region.
                        </li>
                      ) : (
                        rightColumnCountries.map((country) => (
                          <li key={country.id} className="min-h-0 self-start" role="none">
                            <Link href={countryHref(country)} className={countryLinkClass} role="menuitem">
                              {country.name}
                            </Link>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                </>
              ) : (
                <div className="col-span-full flex flex-col">
                  <p className={columnHeadingClass}>Countries</p>
                  <ul
                    className="m-0 grid max-h-[min(60vh,22rem)] auto-rows-min grid-cols-1 list-none gap-x-4 gap-y-0 overflow-y-auto p-0 sm:grid-cols-2 sm:items-start"
                    role="none"
                  >
                    {tourCountries.map((country) => (
                      <li key={country.id} className="min-h-0 self-start" role="none">
                        <Link href={countryHref(country)} className={countryLinkClass} role="menuitem">
                          {country.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
